import FormField from '../../components/field/FormField';
import { AccessLevel } from '../../enums/AccessLevel';
import { TeamMemberPermission } from '../../types/Team';
import { BaseSyntheticEvent, useEffect, useState } from 'react';
import { apiUrl } from '../../api/apiUrl';
import http from '../../api/axios';
import { handleApiErrors } from '../../api/apiErrorHandler';
import { useCurrentCompany } from '../../hooks/useCompany';
import { toast } from 'react-toastify';
import { Loader } from '../../components/loader/Loader';
import { convertEnumToArray, ObjectFromEnum } from '../../utils/functions/data.functions';
import { useLoading } from '../../context/loadingContext';

interface Props {
  teamMemberId: number;
}

/**
 * Component to update permissions for team members
 */
export const TeamMemberPermissionForm = ({ teamMemberId }: Props) => {
  const { company } = useCurrentCompany();
  const [permissions, setPermissions] = useState<TeamMemberPermission[] | null>(null);
  const { startLoading, stopLoading } = useLoading();
  /**
   * Loads permission adjustments for specific team member
   */
  const fetchPermissions = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.TEAM_MEMBER_PERMISSION_ADJUSTMENT_LIST}?parentId=${teamMemberId}`,
    });
    setPermissions(response?.data);
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  /**
   * Creates permission for team member
   * @param accessLevel - Read | Write | Admin
   */
  const handleCreatePermission = async (accessLevel: string) => {
    if (!company) return;
    try {
      startLoading();
      const response = await http.apiPost({
        url: apiUrl.TEAM_MEMBER_PERMISSION_ADJUSTMENT_CREATE,
        data: { companyId: company.id, teamMemberId, accessLevel: accessLevel },
      });

      if (response?.status === 201) {
        stopLoading();
        toast.success('Success!');
        fetchPermissions();
      }
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  /**
   * Removes permission for team member
   * @param accessLevel - Read | Write | Admin
   */
  const handleRemovePremission = async (accessLevel: string) => {
    try {
      const permissionId = permissions?.find(
        (p: TeamMemberPermission) => p.accessLevel === accessLevel,
      )?.id;
      if (!permissionId) return;

      const response = await http.apiDelete({
        url: `${apiUrl.TEAM_MEMBER_PERMISSION_ADJUSTMENT_DELETE}?id=${permissionId}`,
      });

      if (response?.status === 204) {
        toast.success('Success!');
        fetchPermissions();
      }
    } catch (err) {
      handleApiErrors(err);
    }
  };

  /**
   * Handles checkbox event for permission - create or remove
   * @param e - checkbox input event
   */
  const handleOnChange = async (e: BaseSyntheticEvent) => {
    if (!e || !e.target || !company) return;
    if (e.target.checked) {
      handleCreatePermission(e.target.id);
    } else {
      handleRemovePremission(e.target.id);
    }
  };
  if (!permissions) return <Loader />;
  return (
    <>
      {convertEnumToArray(AccessLevel).map((i: ObjectFromEnum) => (
        <div key={i.value} className="inline-block text-center px-3 items-center m-auto">
          <label htmlFor={i.value} className="m">
            {i.value}
          </label>
          <FormField
            key={i.value}
            name={i.value}
            id={i.value}
            type="checkbox"
            onChange={handleOnChange}
            checked={
              !!(
                permissions &&
                permissions.filter((p: TeamMemberPermission) => p.accessLevel === i.value).length >
                  0
              )
            }
            className={`w-4 h-4`}
          />
        </div>
      ))}
    </>
  );
};
